/**
 * Client-side terminal output bookmarks. Any rendered line can be
 * pinned (hover → pin button); the pinned set persists in localStorage
 * and is shown inline via /pinned.
 */
import type { TerminalLine } from '@/lib/types'

const STORAGE_KEY = 'terminal_pinned_v1'
const MAX_PINNED = 50

export interface PinnedEntry {
  id: string           // stable hash of content
  content: string      // raw content (incl. §colour markup so it renders)
  type: TerminalLine['type']
  timestamp: string    // original timestamp
  pinnedAt: number     // Date.now()
  note?: string        // optional user annotation
}

export function loadPinned(): PinnedEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) return parsed as PinnedEntry[]
    }
  } catch {}
  return []
}

export function savePinned(entries: PinnedEntry[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(-MAX_PINNED)))
  } catch {}
}

function hashLine(line: TerminalLine): string {
  // Short deterministic hash — enough to dedupe identical lines.
  let h = 5381
  const s = `${line.type}:${line.content}`
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0
  return `p${Math.abs(h).toString(36)}`
}

export function pinLine(line: TerminalLine): PinnedEntry[] {
  const entries = loadPinned()
  const id = hashLine(line)
  if (entries.find(e => e.id === id)) return entries  // already pinned
  const next: PinnedEntry[] = [
    ...entries,
    {
      id,
      content: line.content,
      type: line.type,
      timestamp: line.timestamp,
      pinnedAt: Date.now(),
    },
  ]
  savePinned(next)
  return next
}

export function unpinLine(line: TerminalLine): PinnedEntry[] {
  const id = hashLine(line)
  const next = loadPinned().filter(e => e.id !== id)
  savePinned(next)
  return next
}

export function isPinned(line: TerminalLine): boolean {
  const id = hashLine(line)
  return loadPinned().some(e => e.id === id)
}

export function clearPinned(): void {
  try { localStorage.removeItem(STORAGE_KEY) } catch {}
}

/** Compact markdown rendering for the /pinned command output. */
export function renderPinned(entries: PinnedEntry[]): string {
  if (entries.length === 0) {
    return '  No pinned lines yet. Hover any output line and click the star to pin it.'
  }
  const chunks: string[] = [
    `  ${entries.length} pinned line${entries.length === 1 ? '' : 's'} (most recent first):`,
    '',
  ]
  for (const e of [...entries].reverse()) {
    const when = new Date(e.pinnedAt).toLocaleString('en-GB', { hour12: false })
    chunks.push(`  [${when}]  (${e.type})`)
    for (const row of e.content.split('\n')) {
      chunks.push(`    ${row}`)
    }
    chunks.push('')
  }
  chunks.push('  /pinned clear to wipe, /pinned rm ID to remove one')
  return chunks.join('\n')
}
